import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { h1, h2, paragraphLarge } from '@helpers/typography';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  --fgColor: ${brand.global.white};
  --bgColor: ${brand.primary.purple.base};

  position: relative;
  z-index: 2;
  color: var(--fgColor);
  background-color: var(--bgColor);

  &.neutral {
    --fgColor: ${brand.primary.base};
    --bgColor: ${brand.tertiary.nearlyWhite.light};

    .post-detail__snippets {
      color: ${brand.primary.purple.base};
    }

    a:has(h2) {
      display: inline-block;
      color: ${brand.primary.purple.base};
      text-decoration: none;
      [data-split-layout='true'] & {
        max-width: var(--maxTextLength);
      }

      &:hover,
      &:focus-within {
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    }
  }

  .post-detail__snippets {
    color: var(--fgColor);
  }
`;
const Inner = styled.div `
  @media ${from(Device.TabletLarge)} {
    ${baseGrid}
    align-items: center;
  }

  @media ${until(Device.TabletLarge)} {
    &:has([data-photo='true']) {
      display: grid;
      text-align: center;
    }
  }
`;
const Title = styled.h1 `
  ${h1}
  [data-split-layout='true'] & {
    max-width: var(--maxTextLength);
  }

  a & {
    ${h2}

    .neutral & {
      color: ${brand.primary.purple.base};
    }
  }

  .secondary-4 &,
  .secondary-5 &,
  .tertiary-4 & {
    color: ${brand.primary.purple.base};
  }
`;
const Content = styled.div `
  ${paragraphLarge}
  [data-split-layout='true'] & {
    max-width: var(--maxTextLength);
  }

  .secondary-4 &,
  .secondary-5 &,
  .tertiary-4 & {
    color: ${brand.global.black};
  }

  p {
    ${paragraphLarge}
  }
`;
const Details = styled.div `
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 14px 40px;
  margin: 0px 0 30px;

  @media ${from(Device.TabletLarge)} {
    flex-direction: row;
    margin: -5px 0 42px;
  }

  strong {
    ${srOnly}
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 14px;
    color: var(--fgColor);
    text-decoration: none;

    svg {
      width: 18px;
      height: 18px;
    }

    &:hover,
    &:focus-visible {
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }
`;
const ContentWrapper = styled.div `
  @media ${from(Device.TabletLarge)} {
    [data-split-layout='false'] & {
      grid-column: auto / span 24;
    }

    [data-split-layout='true'] & {
      grid-column: auto / span 12;
    }
  }

  button[data-type='white'] {
    border: 2px solid var(--colorPrimary);
  }
`;
const CtaWrapper = styled.div `
  margin-top: ${fluid(24, 34)};
`;
const ImageWrapper = styled.div `
  display: flex;
  place-items: center;
  place-content: center;

  &:is([data-photo='true']) {
    @media ${until(Device.TabletLarge)} {
      order: -1;
      margin-bottom: 40px;
      padding: 0 50px;
    }
  }

  &:not([data-photo='true']) {
    margin: -24px 0;
  }

  @media ${from(Device.TabletLarge)} {
    &:is([data-photo='true']),
    &:not([data-photo='true']) {
      margin: unset;
    }

    [data-split-layout='true'] & {
      grid-column: auto / span 12;
    }
  }

  &[data-photo='true'] img {
    border: 15px solid rgba(255, 255, 255, 0.28);
    border-radius: 50%;
  }

  img {
    aspect-ratio: 1 / 1;
    object-fit: contain;
    object-position: center;
  }
`;
const BackgroundShapes = styled.div `
  --shapeOneColor: #480051;
  --shapeTwoColor: #8f4689;

  position: absolute;
  z-index: -1;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const ShapeOne = styled.div `
  position: absolute;
  top: 0;
  right: 0;
  width: 70%;
  height: 150%;
  background-color: var(--shapeOneColor);
  clip-path: ellipse(70% 120% at 100% 50%);
`;
const ShapeTwo = styled.div `
  position: absolute;
  top: 36%;
  right: 0;
  width: 85%;
  height: 100%;
  background-color: var(--shapeTwoColor);
  clip-path: ellipse(70% 100% at 100% 100%);
`;
export default {
    Container,
    Inner,
    Title,
    Content,
    Details,
    ContentWrapper,
    CtaWrapper,
    ImageWrapper,
    BackgroundShapes,
    ShapeOne,
    ShapeTwo,
};
